import React from "react";
import {BaseModelType, IdListRequest, ListRequest} from "../../../app/api/types";
import BasicAsyncAutocomplete, {BasicAsyncAutocompleteProps} from ".";
import {Firmware, FirmwareField, FirmwareState} from "../../../app/api/model/firmware";
import {useFirmwareListQuery} from "../../../app/api/api";
import {Box, Chip, Grid, Tooltip} from "@mui/material";
import {TranslationContext, getTranslation} from "../../../utils/getTranslationKey";
import {useTranslation, TFunction} from "react-i18next";

export type AsyncListAutocompleteProps<T extends BaseModelType> = Omit<
    BasicAsyncAutocompleteProps<T, ListRequest>,
    "additionalRequestParams"
>;

export function AsyncListAutocomplete<T extends BaseModelType>(props: AsyncListAutocompleteProps<T>) {
    return <BasicAsyncAutocomplete<T, ListRequest> additionalRequestParams={{}} {...props} />;
}

export interface AsyncIdListAutocompleteProps<T extends BaseModelType>
    extends Omit<BasicAsyncAutocompleteProps<T, IdListRequest>, "additionalRequestParams"> {
    queryId: number;
}

export function AsyncIdListAutocomplete<T extends BaseModelType>({queryId, ...rest}: AsyncIdListAutocompleteProps<T>) {
    return <BasicAsyncAutocomplete<T, IdListRequest> additionalRequestParams={{id: queryId}} {...rest} />;
}

export type FirmwareAutocompleteProps = Omit<
    AsyncListAutocompleteProps<Firmware>,
    "queryHook" | "queryField" | "getOptionLabel"
>;

interface ColorSetting {
    text: string;
    background: string;
}

const firmwareStateColorMapping: Record<FirmwareState, ColorSetting> = {
    [FirmwareState.DEPRECATED]: {text: "#595959", background: "#cccccc"},
    [FirmwareState.DEVELOPMENT]: {text: "#000000", background: "#ccccff"},
    [FirmwareState.ALPHA]: {text: "#000000", background: "#ffffcc"},
    [FirmwareState.BETA]: {text: "#000000", background: "#ccffff"},
    [FirmwareState.STABLE]: {text: "#000000", background: "#ccffcc"},
} as const;

function renderFirmwareOption(t: TFunction) {
    return (props: React.HTMLAttributes<HTMLLIElement>, option: Firmware): React.ReactNode => {
        return (
            <li {...props}>
                <Tooltip
                    title={`${option[FirmwareField.VERSION_NUMBER]} (${getTranslation({
                        context: TranslationContext.FIRMWARE_STATE,
                        value: option[FirmwareField.STATE],
                        t,
                    })}${option[FirmwareField.LEGACY] ? " - " + t("generic_legacy") : ""})`}
                >
                    <Grid container columns={15}>
                        <Grid item xs={8}>
                            <Box
                                sx={{
                                    margin: "auto 0",
                                    fontStyle: option[FirmwareField.LEGACY] ? "italic" : undefined,
                                    color: option[FirmwareField.LEGACY] ? "#f47818" : "#034176",
                                }}
                            >
                                {option[FirmwareField.VERSION_NUMBER]}
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Chip
                                label={getTranslation({
                                    context: TranslationContext.FIRMWARE_STATE,
                                    value: option[FirmwareField.STATE],
                                    t,
                                })}
                                size="small"
                                sx={{
                                    color:
                                        firmwareStateColorMapping[option[FirmwareField.STATE] as FirmwareState]?.text ||
                                        "#000000",
                                    backgroundColor:
                                        firmwareStateColorMapping[option[FirmwareField.STATE] as FirmwareState]
                                            ?.background || "#bbbbbb",
                                    borderColor: "#999999",
                                    minWidth: "100px",
                                    maxWidth: "100px",
                                }}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            {option[FirmwareField.LEGACY] && (
                                <Chip
                                    label={t("generic_legacy")}
                                    sx={{
                                        minWidth: "75px",
                                        maxWidth: "75px",
                                        color: "#f47818",
                                        borderColor: "#f47818",
                                    }}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Grid>
                    </Grid>
                </Tooltip>
            </li>
        );
    };
}

export function FirmwareAutocomplete({renderOption, ...rest}: FirmwareAutocompleteProps) {
    const {t} = useTranslation();
    return (
        <AsyncListAutocomplete<Firmware>
            queryHook={useFirmwareListQuery}
            queryField={FirmwareField.VERSION_NUMBER}
            renderOption={renderOption || renderFirmwareOption(t)}
            sort={`-${FirmwareField.VERSION_NUMBER}`}
            {...rest}
        />
    );
}
